// src/components/Sidebar.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon,
  Settings as SettingsIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  List as ListIcon,
  Engineering as EngineeringIcon,
  NetworkCheck as NetworkIcon,
  BarChart as StatsIcon,
  Receipt as ReceiptIcon,
  Pending as PendingIcon,
  Report as ReportIcon,
  Payment as PaymentMethodIcon,
  Router as RouterIcon,
  Speed as SpeedIcon,
  LocationOn as ZoneIcon,
  NetworkWifi as AccessPointIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  TechniciansIcon,
  SupportAgent as SupportIcon,
  QuestionAnswer as TicketsIcon,
  MenuBook as KnowledgeIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Warehouse as WarehouseIcon,
  SwapHoriz as SwapHorizIcon,
  ShoppingCart as ShoppingCartIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  FmdGood as LocationIcon,
  Map as MapIcon,
  Explore as ExploreIcon,
  SatelliteAlt as DeviceIcon,
  Link as LinkIcon,
  Add as AddIcon,
  Assignment as AssignmentIcon,
  Sync as SyncIcon,
  WhatsApp as WhatsAppIcon,
  TrendingDown as EgresosIcon,
  AccountBalance as ContabilidadIcon,
  Lan as LanIcon,
  CardGiftcard as GiftIcon,
  Language as WebIcon,
  Build as BuildIcon
} from '@mui/icons-material';

function Sidebar({ isOpen, onClose, onOpen }) {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState('');
  const [hideAllSubmenus, setHideAllSubmenus] = useState(false);
  const { hasPermission, user } = useAuth();
  const [companySettings, setCompanySettings] = useState({ company_name: 'YinoWISP', company_logo: '' });

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await fetch('/api/public/company-settings');
        if (response.ok) {
          const data = await response.json();
          setCompanySettings({
            company_name: data.company_name || 'YinoWISP',
            company_logo: data.company_logo || ''
          });
        }
      } catch (error) {
        console.error('Error fetching public company settings for sidebar:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  const handleLinkClick = () => {
    setOpenMenu('');
    setHideAllSubmenus(true);
    setTimeout(() => {
      setHideAllSubmenus(false);
    }, 500);
    if (window.innerWidth <= 768 && onClose) {
      onClose();
    }
  };

  useEffect(() => {
    if (isOpen) return;
    if (!openMenu) return;

    const handleOutsideClick = (event) => {
      const sidebarElement = document.getElementById('admin-sidebar');
      if (sidebarElement && !sidebarElement.contains(event.target)) {
        setOpenMenu('');
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isOpen, openMenu]);

  // Identificar si la app se ejecuta en el dominio principal o si el usuario pertenece a la empresa principal
  const hostname = window.location.hostname.toLowerCase();
  const isMainDomain = (hostname.includes('yinowisp') || hostname.startsWith('admin.') || hostname === 'localhost' || hostname === '127.0.0.1');
  
  // Es empresa principal si el tenant es la empresa raíz (Main Tenant)
  const isMainTenant = !user?.tenant_id || user?.tenant_id === '00000000-0000-0000-0000-000000000001' || user?.tenant_slug === 'main';

  // Función para comprobar permiso con gestión de errores y restricción de tenant principal
  const checkPermission = (permission, mainTenantOnly = false) => {
    if (mainTenantOnly && !isMainTenant) return false;
    if (!permission) return true;

    try {
      if (hasPermission && typeof hasPermission === 'function') {
        return hasPermission(permission);
      }

      if (user?.role === 'admin' && user?.isSystemRole === true) {
        return true;
      }

      return user?.permissions?.includes(permission) || false;
    } catch (error) {
      console.error('Error al verificar permiso:', error);
      return false;
    }
  };

  // Estructura del menú
  const menuStructure = [
    {
      title: 'Dashboard',
      path: '/admin',
      icon: DashboardIcon,
      permission: null
    },
    {
      title: 'Clientes',
      icon: PeopleIcon,
      permission: 'clients.view',
      submenu: [
        { title: 'Lista de Clientes', path: '/clients/list', icon: ListIcon, permission: 'clients.view' },
        { title: 'Nuevo Cliente', path: '/clients/add', icon: AddIcon, permission: 'clients.create' },
        { title: 'Instalaciones', path: '/clients/installations', icon: EngineeringIcon, permission: 'installations.create' },
        { title: 'Mapa de Instalaciones', path: '/clients/installations/map', icon: LocationIcon, permission: 'installations.view' },
        { title: 'Aprobar Instalaciones', path: '/clients/installations/pending', icon: PendingIcon, permission: 'clients.view' },
        { title: 'Tráfico', path: '/clients/traffic', icon: NetworkIcon, permission: 'clients.traffic.view' },
        { title: 'Estadísticas', path: '/clients/statistics', icon: StatsIcon, permission: 'clients.view' },
      ],
    },
    {
      title: 'Finanzas',
      icon: MoneyIcon,
      permission: 'invoices.view',
      submenu: [
        { title: 'Dashboard', path: '/finance/dashboard', icon: DashboardIcon, permission: 'finance.reports.view' },
        { title: 'Facturas', path: '/finance/invoices', icon: ReceiptIcon, permission: 'invoices.view' },
        { title: 'Reporte de Pagos', path: '/finance/payment-reports', icon: ReportIcon, permission: 'finance.reports.view' },
        { title: 'Formas de Pago', path: '/finance/payment-methods', icon: PaymentMethodIcon, permission: 'payments.create' },
        { title: 'Catálogo de Servicios', path: '/finance/billing-services', icon: CategoryIcon, permission: 'invoices.view' },
        { title: 'Promociones', path: '/finance/promotions', icon: GiftIcon, permission: 'invoices.view' },
        { title: 'Egresos y Gastos', path: '/finance/expenses', icon: EgresosIcon, permission: 'finance.reports.view' },
        { title: 'Cajas y Contabilidad', path: '/finance/accounting', icon: ContabilidadIcon, permission: 'finance.reports.view' },
      ],
    },
    {
      title: 'Inventario',
      icon: InventoryIcon,
      permission: 'inventory.view',
      submenu: [
        { title: 'Dashboard', path: '/inventory/dashboard', icon: DashboardIcon, permission: 'inventory.view' },
        { title: 'Productos', path: '/inventory/items', icon: InventoryIcon, permission: 'inventory.view' },
        { title: 'Equipos (S/N)', path: '/inventory/equipment', icon: DeviceIcon, permission: 'inventory.view' },
        { title: 'Packs / Kits', path: '/inventory/kits', icon: ListIcon, permission: 'inventory.view' },
        { title: 'Categorías', path: '/inventory/categories', icon: CategoryIcon, permission: 'inventory.manage' },
        { title: 'Almacenes', path: '/inventory/warehouses', icon: WarehouseIcon, permission: 'inventory.manage' },
        { title: 'Técnicos', path: '/inventory/technicians', icon: AssignmentIcon, permission: 'inventory.manage' },
        { title: 'Movimientos', path: '/inventory/transactions', icon: SwapHorizIcon, permission: 'inventory.transactions.view' },
        { title: 'Compras', path: '/inventory/purchases', icon: ShoppingCartIcon, permission: 'inventory.purchases.view' },
        { title: 'Stock Bajo', path: '/inventory/low-stock', icon: WarningIcon, permission: 'inventory.view' },
        { title: 'Reportes', path: '/inventory/reports', icon: AssessmentIcon, permission: 'inventory.reports.view' },
        { title: 'Mapa de Equipos', path: '/inventory/client-equipment-map', icon: MapIcon, permission: 'inventory.view' },
      ],
    },
    {
      title: 'Mapas de Red',
      icon: ExploreIcon,
      permission: 'clients.view',
      submenu: [
        { title: 'Conexiones AP-CPE', path: '/clients/connection-map', icon: LinkIcon, permission: 'clients.view' },
        { title: 'Mapa de Cobertura', path: '/network/coverage-map', icon: MapIcon, permission: 'clients.view' },
        { title: 'Equipos de Clientes', path: '/clients/equipment-map', icon: DeviceIcon, permission: 'clients.view' },
        { title: 'Técnicos en Vivo', path: '/network/technicians-map', icon: EngineeringIcon, permission: 'clients.view' },
      ],
    },
    {
      title: 'Soporte',
      icon: SupportIcon,
      permission: null,
      submenu: [
        { title: 'Tickets', path: '/support/tickets', icon: TicketsIcon, permission: null },
        { title: 'Base de Conocimientos', path: '/support/knowledge', icon: KnowledgeIcon, permission: null },
      ],
    },
    {
      title: 'Gestión Web (Landing)',
      path: '/system/landing',
      icon: WebIcon,
      permission: 'roles.manage'
    },
    {
      title: 'Gestión de Red',
      icon: LanIcon,
      permission: null,
      submenu: [
        { title: 'Routers', path: '/system/routers', icon: RouterIcon, permission: 'routers.manage' },
        { title: 'Torres/Nodos', path: '/system/nodes', icon: LocationIcon, permission: 'zones.manage' },
        { title: 'AP/Sectoriales', path: '/system/access-points', icon: AccessPointIcon, permission: 'routers.manage' },
        { title: 'Enlaces Troncales (PtP)', path: '/system/ptp-links', icon: SwapHorizIcon, permission: 'routers.manage' },
        { title: 'Zonas', path: '/system/zones', icon: ZoneIcon, permission: 'zones.manage' }
      ],
    },
    {
      title: 'Sistema',
      icon: SettingsIcon,
      permission: null,
      submenu: [
        { title: 'Cola de Sincronización', path: '/system/sync-queue', icon: SyncIcon, permission: 'routers.manage' },
        { title: 'Planes de Internet', path: '/system/internet-plans', icon: SpeedIcon, permission: 'plans.manage' },
        { title: 'Técnicos', path: '/system/technicians', icon: BuildIcon, permission: 'clients.view' },
        { title: 'Roles y Permisos', path: '/system/roles', icon: SecurityIcon, permission: 'roles.manage' },
        { title: 'Usuarios', path: '/system/users', icon: PersonIcon, permission: 'users.view' },
        { title: 'Configuración de Empresa', path: '/system/settings', icon: SettingsIcon, permission: 'roles.manage' },
        { title: 'Pasarelas de Pago', path: '/system/payment-gateways', icon: PaymentMethodIcon, permission: 'roles.manage' },
        { title: 'Avisos en Pantalla', path: '/system/warning-designer', icon: WarningIcon, permission: 'roles.manage' },
        { title: 'Diseñador de PDF', path: '/system/template-editor', icon: ReceiptIcon, permission: 'roles.manage' },
        { title: 'Notificaciones WhatsApp', path: '/system/whatsapp', icon: WhatsAppIcon, permission: 'roles.manage' },
        { title: '☁️ Control SuperAdmin SaaS', path: '/superadmin', icon: SettingsIcon, permission: 'roles.manage', mainTenantOnly: true }
      ],
    },
  ];

  const toggleMenu = (title) => {
    setOpenMenu(prev => prev === title ? '' : title);
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Filtrar el menú basado en permisos y tenant principal
  const filteredMenu = menuStructure.map(item => {
    if (!checkPermission(item.permission, item.mainTenantOnly)) return null;

    if (item.submenu) {
      const filteredSub = item.submenu.filter(subItem => checkPermission(subItem.permission, subItem.mainTenantOnly));
      if (filteredSub.length === 0) return null;
      return { ...item, submenu: filteredSub };
    }

    return item;
  }).filter(Boolean);

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden backdrop-blur-xs transition-opacity duration-300"
          onClick={onClose}
        />
      )}
      <aside 
        id="admin-sidebar"
        onMouseLeave={() => setHideAllSubmenus(false)}
        className={`
          fixed inset-y-0 left-0 z-[1100] flex flex-col bg-gray-800 text-white h-screen transition-all duration-300
          ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64 md:translate-x-0 md:w-20'}
          md:relative md:flex
        `}
      >
      <div className="p-4 flex-shrink-0 border-b border-gray-700/30">
        <div className="flex items-center gap-3">
          {companySettings.company_logo ? (
            <img 
              src={companySettings.company_logo} 
              alt="Logo" 
              className={`h-8 w-auto object-contain ${!isOpen ? 'md:mx-auto' : ''}`} 
            />
          ) : (
            <div className={`h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white flex-shrink-0 ${!isOpen ? 'md:mx-auto' : ''}`}>
              {companySettings.company_name.charAt(0).toUpperCase()}
            </div>
          )}
          {isOpen && (
            <div className="min-w-0 flex-1">
              <h1 className="text-sm font-bold truncate text-white" title={companySettings.company_name}>
                {companySettings.company_name}
              </h1>
              <p className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Admin Panel</p>
            </div>
          )}
        </div>
        {isOpen && user && (
          <div className="mt-3 py-2 px-2.5 bg-gray-900/50 rounded-lg border border-gray-700/20 flex items-center gap-3">
            {user.avatar ? (
              <img 
                src={user.avatar} 
                alt="Profile" 
                className="h-8 w-8 rounded-full object-cover ring-2 ring-blue-500/30" 
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center font-bold text-white text-xs flex-shrink-0">
                {user.username.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-200 truncate">
                {user.username}
              </p>
              <p className="text-[10px] text-gray-400 capitalize truncate">
                {user.role === 'admin' ? 'Administrador' : user.role || 'Usuario'}
              </p>
            </div>
          </div>
        )}
      </div>
      <nav className={`mt-2 flex-grow ${isOpen ? 'overflow-y-auto dark-scrollbar' : 'overflow-hidden md:overflow-visible'}`}>
        {filteredMenu.map((item, index) => (
          <div key={index} className="mb-2 relative group w-full">
            {item.submenu ? (
              <>
                <button
                  onClick={() => toggleMenu(item.title)}
                  className={`w-full px-4 py-3 flex items-center ${
                    isOpen ? 'justify-between' : 'justify-center'
                  } text-gray-300 hover:text-white hover:bg-gray-700 transition-colors ${item.submenu.some(subItem => isActive(subItem.path)) ? 'bg-gray-700 text-white' : ''
                    }`}
                  title={!isOpen ? item.title : ''}
                >
                  <div className="flex items-center">
                    <item.icon className={`h-5 w-5 ${!isOpen && 'md:mx-auto'}`} />
                    {isOpen && <span className="ml-3">{item.title}</span>}
                  </div>
                  {isOpen && (
                    openMenu === item.title ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  )}
                </button>
                {(isOpen ? openMenu === item.title : true) && (
                  isOpen ? (
                    <div className={`bg-gray-900/95 backdrop-blur-md py-2 ${hideAllSubmenus ? 'hidden' : 'block'}`}>
                      {item.submenu
                        .filter(subItem => checkPermission(subItem.permission, subItem.mainTenantOnly))
                        .map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            onClick={handleLinkClick}
                            className={`flex items-center px-4 py-2 text-sm ${isActive(subItem.path)
                              ? 'bg-gray-700 text-white'
                              : 'text-gray-400 hover:text-white hover:bg-gray-700'
                              } pl-12`}
                          >
                            <subItem.icon className="h-4 w-4 mr-3" />
                            {subItem.title}
                          </Link>
                        ))}
                    </div>
                  ) : (
                    <div 
                      className={`
                        absolute left-20 ${index >= filteredMenu.length - 2 ? 'bottom-0' : 'top-0'} ml-1 w-56 z-50 
                        hidden md:group-hover:block 
                        before:content-[''] before:absolute before:top-0 before:bottom-0 before:-left-8 before:w-8
                        ${openMenu === item.title ? 'md:!block' : ''}
                        ${hideAllSubmenus ? '!hidden' : ''}
                      `}
                    >
                      <div 
                        className="bg-gray-900/95 backdrop-blur-md rounded-xl border border-gray-700/50 py-2 shadow-2xl overflow-y-auto dark-scrollbar"
                        style={{
                          maxHeight: index >= filteredMenu.length - 2
                            ? `${80 + index * 55}px`
                            : `calc(100vh - ${100 + index * 55}px)`
                        }}
                      >
                        <div className="px-4 py-2 border-b border-gray-700/30 text-xs font-bold text-blue-400 uppercase tracking-wider mb-1">
                          {item.title}
                        </div>
                        {item.submenu
                          .filter(subItem => checkPermission(subItem.permission, subItem.mainTenantOnly))
                          .map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.path}
                              onClick={handleLinkClick}
                              className={`flex items-center px-4 py-2 text-sm ${isActive(subItem.path)
                                ? 'bg-gray-700 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-gray-700'
                                } pl-4`}
                            >
                              <subItem.icon className="h-4 w-4 mr-3" />
                              {subItem.title}
                            </Link>
                          ))}
                      </div>
                    </div>
                  )
                )}
              </>
            ) : (
              <Link
                to={item.path}
                onClick={handleLinkClick}
                className={`px-4 py-3 flex items-center ${
                  isOpen ? 'justify-start' : 'justify-center'
                } text-gray-300 hover:text-white hover:bg-gray-700 transition-colors ${isActive(item.path) ? 'bg-gray-700 text-white' : ''
                  }`}
                title={!isOpen ? item.title : ''}
              >
                <item.icon className={`h-5 w-5 ${!isOpen ? 'md:mx-auto' : 'mr-3'}`} />
                {isOpen && <span>{item.title}</span>}
              </Link>
            )}
          </div>
        ))}
      </nav>
    </aside>
    </>
  );
}

export default Sidebar;